/**
 * usePasswordReveal — temporaeres Anzeigen von Passwoertern.
 *
 * Nach dem Aufdecken laeuft ein Countdown (default 10 s). Sobald er abgelaufen
 * ist, wird das Passwort automatisch wieder ausgeblendet — verhindert
 * Schulter-Surfen und vergessene Sicht-Toggle.
 *
 * Usage:
 *   const reveal = usePasswordReveal({ totalMs: 10000 });
 *   reveal.isVisible(rowId)       // boolean
 *   reveal.show(rowId)            // start countdown
 *   reveal.hide(rowId)            // sofort wieder ausblenden
 *   reveal.remainingPct(rowId)    // 0..100 fuer Countdown-Ring
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const TICK_MS = 250;

export const usePasswordReveal = ({ totalMs = 10000 } = {}) => {
  // visible: { [id]: revealedAtMs }
  const [visible, setVisible] = useState({});
  const [now, setNow] = useState(Date.now());
  const tickRef = useRef(null);

  // Ticker laeuft nur wenn mindestens ein Eintrag sichtbar ist
  useEffect(() => {
    const hasAny = Object.keys(visible).length > 0;
    if (!hasAny) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    if (!tickRef.current) {
      tickRef.current = setInterval(() => setNow(Date.now()), TICK_MS);
    }
    return () => {
      if (tickRef.current && Object.keys(visible).length === 0) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [visible]);

  // Auto-Hide: bei jedem Tick abgelaufene Eintraege entfernen
  useEffect(() => {
    setVisible((prev) => {
      const next = {};
      let changed = false;
      for (const [id, ts] of Object.entries(prev)) {
        if (now - ts < totalMs) next[id] = ts;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [now, totalMs]);

  const show = useCallback((id) => {
    setVisible((prev) => ({ ...prev, [id]: Date.now() }));
  }, []);

  const hide = useCallback((id) => {
    setVisible((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const toggle = useCallback((id) => {
    setVisible((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: Date.now() };
    });
  }, []);

  const isVisible = useCallback((id) => !!visible[id], [visible]);

  const remainingPct = useCallback((id) => {
    if (!visible[id]) return 0;
    const elapsed = now - visible[id];
    return Math.max(0, Math.min(100, ((totalMs - elapsed) / totalMs) * 100));
  }, [visible, now, totalMs]);

  return { isVisible, show, hide, toggle, remainingPct };
};

export default usePasswordReveal;
